// utils/jsonImporter.js



/// required for converting to beds and area units in commercial details

// --- Setup: size units and normalization ---
const sizeTypes = {
  "Square Yards": ["Y", "YD", "YARD", "YARDS"],
  "Square Meters": ["M", "METER", "METERS", "MTR", "MTRS", "METRE","SQ MT","SQ M"],
  "Square Feet": ["FT", "FEET", "FOOT","SQFT", "SFT", "SF","SQ FT","sq ft"],
  Acre: ["acre", "acres"],
};

const unitNormalizationMap = new Map();
const allSizeUnits = [];

let projectStatuses=[];
let projectCompletions=[];


for (const key in sizeTypes) {
  sizeTypes[key].forEach((unit) => {
    const lowerCaseUnit = unit.toLowerCase();
    allSizeUnits.push(lowerCaseUnit);
    unitNormalizationMap.set(lowerCaseUnit, key);
  });
}

// Regex to find a number + unit in a string
const sizeStringRegex = new RegExp(`(\\d+(?:\\.\\d+)?)\\s*(${allSizeUnits.join("|")})\\b`, "i");

// Regex to find number of beds in a string
const roomUnitMap = {
  bhk: "BHK",
  br: "BR",
  beds: "Beds",
  bed: "Beds",
};
const roomRegex = new RegExp(`(\\d+)\\s*(${Object.keys(roomUnitMap).join("|")})\\b`, "i");



/// required for converting to beds and area units in commercial details



export const normalize = (str) =>
  String(str || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

export const getValueFromPath = (obj, path) => {
  if (!obj || !path) return null;

  const keys = path.split(".");

  const walk = (current, index) => {
    if (current === undefined || current === null) return null;
    if (index === keys.length) return current;

    let key = keys[index];

    if (key.includes("[]")) {
      key = key.replace("[]", "");
      const arr = current[key];
      if (!Array.isArray(arr)) return null;
      return arr.map((item) => walk(item, index + 1));
    }

    return walk(current[key], index + 1);
  };

  return walk(obj, 0);
};

export const findKeyWithAlias = (sourceObj, attr) => {
  const keys = Object.keys(sourceObj);

  let key = keys.find(
    (k) => normalize(k) === normalize(attr.json_attribute)
  );

  if (key) return sourceObj[key];

  if (!attr.alias) return null;

  for (let alias of attr.alias) {
    if (!alias) continue;

    if (alias.includes(".") || alias.includes("[]")) {
      const val = getValueFromPath(sourceObj, alias);
      if (val !== undefined && val !== null) return val;
    }
  }

  for (let alias of attr.alias) {
    if (!alias) continue;

    const key = keys.find(
      (k) => normalize(k) === normalize(alias)
    );

    if (key) return sourceObj[key];
  }

  return null;
};

export const getFinalValue = (value, type) => {
  if (value === undefined || value === null) return null;

  switch (type) {
    case "string":
      if (typeof value === "object") {
        return value?.label ?? value?.text ?? null;
      }
      return String(value);

    case "number":
      return Number(value);

    case "array":
      if (typeof value === "string") {
        try {
          value = JSON.parse(value);
        } catch {
          value = [value];
        }
      }
      if (!Array.isArray(value)) value = [value];
      return value.flat(Infinity);

    case "object":
      if (typeof value === "string") {
        try {
          return JSON.parse(value);
        } catch {
          return null;
        }
      }
      return value;

    default:
      return value;
  }
};

export const setPayloadValue = (obj, path, value) => {
  const keys = path.split(".");
  let temp = obj;

  keys.forEach((key, index) => {
    const arrMatch = key.match(/(.*)\[(\d*)\]/);

    if (arrMatch) {
      const arrKey = arrMatch[1];
      const idx = parseInt(arrMatch[2], 10);

      if (!temp[arrKey]) temp[arrKey] = [];

      if (index === keys.length - 1) {
        temp[arrKey][idx] = value;
      } else {
        if (!temp[arrKey][idx]) temp[arrKey][idx] = {};
        temp = temp[arrKey][idx];
      }
    } else {
      if (index === keys.length - 1) {
        temp[key] = value;
      } else {
        if (!temp[key]) temp[key] = {};
        temp = temp[key];
      }
    }
  });
};

export const processSourceJson = ({
  sourceJson,
  AcresMaster,
  log,
  fileName = null
}) => {

  let validate = true;
  let finalPayload = [];

  const nodes = ["newProjects", "secondaryNewProjects", "properties"];

  nodes.forEach((nodeName) => {

    const nodeData = sourceJson[nodeName];

    if (!nodeData) {
      log?.(`⚠ ${fileName || "INPUT"}: Node not found ${nodeName}`);
      return;
    }

    nodeData.forEach((project, rowIndex) => {

      const rowPayload = {};

      if (fileName) {
        rowPayload.fileName = fileName;
      }

      Object.keys(AcresMaster).forEach((section) => {

        AcresMaster[section].forEach((attr) => {

          let value = findKeyWithAlias(project, attr);

          if (attr.transform) {
            value = attr.transform(value);
          }

          value = getFinalValue(value, attr.type);

          if (
            attr.mandatory &&
            (value === null || value === undefined)
          ) {
            validate = false;
            log?.(`❌ ${fileName || "INPUT"} Row ${rowIndex} Missing: ${attr.json_attribute}`);
          }

          if (attr.payload_attribute) {
            setPayloadValue(rowPayload, attr.payload_attribute, value);
          } else {
            rowPayload[attr.json_attribute.toLowerCase()] = value;
          }

        });
      });

      // 🔥 Step 2: enrichment (media comes here)
        const projectId = project?.primedetails?.projectid;
        // console.log("project: ", project);
      
        rowPayload.media = extractMedia(rowPayload, projectId);
        rowPayload.commercialdetails = extractCommercialDetails(rowPayload);
        rowPayload.possessiondetails = extractPossession(rowPayload);
        // 🔹 Step 3: push final object
        finalPayload.push(rowPayload);


    });
  });

  // console.log("Project Statuses found: ", projectStatuses);
  return { validate, data: finalPayload };
};


// Predefined main statuses
const possibleStatuses = ["New Launch", "Ready to Move", "Under Construction"];
// Build regex to match main status + optional bracket info
const escapedStatuses = possibleStatuses.map(s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
const statusRegex = new RegExp(`^(${escapedStatuses.join("|")})(?:\\s*\\((.*?)\\))?`);



function extractPossession(projectrow) {
  const possession = projectrow?.possession;
  if (!possession) return null;

  const rawStatus = (possession?.status ?? "").trim();
  const completionRaw = possession?.possessiondate ?? null;

  let estatus = null;
  let status = null;

  // ✅ Check if rawStatus contains any of the possibleStatuses
  const foundStatus = possibleStatuses.find(s => rawStatus.toLowerCase().includes(s.toLowerCase()));

  if (foundStatus) {
    estatus = foundStatus;                          // Main category
    status = rawStatus.replace(foundStatus, "").trim(); // Remainder
    // Remove surrounding brackets if any
    if (status.startsWith("(") && status.endsWith(")")) {
      status = status.slice(1, -1).trim();
    }
  } else {
    // No known status found, treat entire string as estatus
    estatus = rawStatus;
    status = null;
  }

  // ✅ Add to projectStatuses only if estatus not in possibleStatuses and not already present
  if (!possibleStatuses.some(s => s.toLowerCase() === estatus.toLowerCase())
      && !projectStatuses.includes(estatus)) {
    projectStatuses.push(estatus);
  }

  // ✅ Extract completion date
// Extract possession date as a full Date object
  let possessionDate = null;
  if (completionRaw) {
    const matchDate = completionRaw.match(/Completion in ([A-Za-z]+),\s*(\d{4})/);
    if (matchDate) {
          const [, month, year] = matchDate;
          const date = new Date(`${month} 1, ${year}`);
          if (!isNaN(date)) {
            // Convert to YYYY-MM-DD format
            const yyyy = date.getFullYear();
            const mm = String(date.getMonth() + 1).padStart(2, "0"); // month is 0-indexed
            const dd = String(date.getDate()).padStart(2, "0");
            possessionDate = `${yyyy}-${mm}-${dd}`; // e.g., "2030-11-01"
          }
        }
  }

  return { status: estatus, completion: possessionDate };
}



/**
 * Extract media from all projects in an array
 * @param {Array} projects - Array of project objects
 * @returns {Array} - Flat array of media items with projectId
 */
export const extractAllProjectsImages = (projects = []) => {
  const allProjects = [];
  // console.log("insside extractAllProjectsImages: ", projects);

  projects.forEach((project) => {
    // Evaluate projectId from primedetails
    const projectId = project?.primedetails?.projectid ||  null;

    // if (!projectId) return; // skip if no projectId

    // Extract media for this project
    const media = extractMedia(project, projectId);

      project.media = media;
    // Push all media into the master array
    allProjects.push(project);
    // return allProjects;
  });

  // console.log("with images: ", allProjects);
  return allProjects;
};


// const getCaseInsensitive = (obj, key) => {
//   const foundKey = Object.keys(obj).find(
//     k => k.toLowerCase() === key.toLowerCase()
//   );
//   return foundKey ? obj[foundKey] : undefined;
// };



export const extractCommercialDetails = (projectRow) => {
  try {
    const getCaseInsensitive = (obj, key) => {
      if (!obj) return undefined;
      const foundKey = Object.keys(obj).find(
        k => k.toLowerCase() === key.toLowerCase()
      );
      return foundKey ? obj[foundKey] : undefined;
    };

    const data = getCaseInsensitive(projectRow, "project_summary_json");
    if (!Array.isArray(data)) return [];

    return data.map((item, index) => {
      if (!item) return null;

      // 1️⃣ Extract raw strings for parser
      // const bedStr =
      //   item?.configInfo?.label ??  // nested type
      //   item?.BEDROOM ??             // flat type
      //   null;

      const bedStr = item?.configInfo?.label ?? item?.BEDROOM ?? null;
      const propertytypeStr=item?.configInfo?.subLabel ?? item?.PROPERTY_TYPE ?? null;

      // const areaStr =
      //   item?.area?.display ??      // nested type
      //   item?.MIN_AREA              // flat type numeric min (will convert to string)
      //     ? `${item.MIN_AREA} - ${item.MAX_AREA} ${item.AREA_UNIT ?? "sq ft"}`
      //     : null;
      let nested=true;
      if (item?.area && item?.price && item?.configInfo) {
        nested=true;
      } else {
        nested=false;
        // console.log(" 2️⃣not nested ")
      }

      if (!item?.area){
        // console.log(" 2️⃣ Area Attribute missing ")

      }

      let areaMissing=false;

      const areaStr = item?.area?.display
        ? item.area.display
        : (item?.AREA_UNIT)
        ? item.AREA_UNIT
        : null;


      // const areaStr = item?.area?.display
      //   ? item.area.display
      //   : (item?.MIN_AREA != null && item?.MAX_AREA != null)
      //   ? `${item.MIN_AREA} - ${item.MAX_AREA} ${item.AREA_UNIT ?? "sq ft"}`
      //   : null;

      if ( !areaStr || !bedStr ) {
        areaMissing=true;
        // console.warn(`Missing area or bed info in commercial details at index ${index}:`, { areaStr, bedStr, item ,projectRow});
      }

      // 2️⃣ Parse number of beds and area unit
      const result = extractSizeAndBeds(areaStr, bedStr);

      if (result.sizeUnit === null && !areaMissing) {
         console.warn(`Failed to extract beds or size unit at index ${index}:`, { areaStr, bedStr, result ,item,projectRow});
      }

      // 3️⃣ Return unified object
      return {
        priceMin: Number(item?.price?.min ?? item?.MIN_PRICE) || null,
        priceMax: Number(item?.price?.max ?? item?.MAX_PRICE) || null,
        areaMin: item?.area?.min ?? item?.MIN_AREA ?? null,
        areaMax: item?.area?.max ?? item?.MAX_AREA ?? null,

        propertyType:propertytypeStr,

        beds: result.noOfBeds,
        areaUnit: result.sizeUnit,            // extracted by parser
        areaDisplay: areaStr,                 // raw string
        areaDisplayMetric: item?.area?.secondaryDisplay ?? null
      };
    }).filter(Boolean);

  } catch (err) {
    console.error("extractCommercialDetails FAILED:", err, projectRow);
    return [];
  }
};



/**
 * Extract only the size unit and number of beds from strings
 * @param {string} areaStr - area display string, e.g., "2,085 - 2,419 sqft"
 * @param {string} bedsStr - bed string, e.g., "3 BHK"
 * @returns {object} { sizeUnit: string|null, noOfBeds: number|null }
 */
// export const extractSizeAndBeds = (areaStr, bedsStr) => {
//   // Ensure safe strings
//   const safeAreaStr = typeof areaStr === "string" ? areaStr : "";
//   const safeBedsStr = typeof bedsStr === "string" ? bedsStr : "";

//   // console.log("Parsing area and beds:", { areaStr: safeAreaStr, bedsStr: safeBedsStr });

//   // Extract size unit
//   const unitMatch = safeAreaStr.match(sizeStringRegex);

//   // console.log("Size string match result:", unitMatch);



//   const sizeUnit = unitMatch
//     ? unitNormalizationMap.get(unitMatch[2]?.toLowerCase())
//     : null;

//   // Extract number of beds
//   const roomMatch = safeBedsStr.match(roomRegex);
//   const noOfBeds = roomMatch ? parseInt(roomMatch[1]) : null;

//   return { sizeUnit, noOfBeds };
// };




export const extractSizeAndBeds = (areaStr, bedsStr) => {
  const safeAreaStr = typeof areaStr === "string" ? areaStr : "";
  const safeBedsStr = typeof bedsStr === "string" ? bedsStr : "";

  // Extract size unit from string
  let sizeUnit = null;
  const unitMatch = safeAreaStr.match(sizeStringRegex);
  if (unitMatch) {
    sizeUnit = unitNormalizationMap.get(unitMatch[2]?.toLowerCase()) || null;
  } else {
    // fallback: check if string contains any known unit
    for (const [unitLower, normalized] of unitNormalizationMap.entries()) {
      if (safeAreaStr.toLowerCase().includes(unitLower)) {
        sizeUnit = normalized;
        break;
      }
    }
  }

  // Extract number of beds
  const roomMatch = safeBedsStr.match(roomRegex);
  const noOfBeds = roomMatch ? parseInt(roomMatch[1]) : null;

  return { sizeUnit, noOfBeds };
};


// extract urls of images, documents in 4 attributes 
const extractFileName = (url) => {
  if (!url) return null;
  return url.split("/").pop().split("?")[0];
};

const isValidUrl = (url) =>
  typeof url === "string" &&
  (url.startsWith("http://") || url.startsWith("https://"));

const extractMedia = (project, projectId) => {
  const media = [];

  const { imagedetails, otherimages, brochure, videos } = project;

  // 🔹 imagedetails
  if (imagedetails) {
    const { main_image, main_image_alt_tag, projectlogo } = imagedetails;

    // main image
    if (isValidUrl(main_image)) {
      media.push({
        sourceurl: main_image,
        projectid: projectId,
        attributename: "imagedetails",
        imagename: extractFileName(main_image),
        alt_tag: main_image_alt_tag || null,
        prime_image: true,
        project_logo: false,
        file_type: "image",
      });
    }

    // project logo
    if (isValidUrl(projectlogo)) {
      media.push({
        sourceurl: projectlogo,
        projectid: projectId,
        attributename: "imagedetails",
        imagename: extractFileName(projectlogo),
        alt_tag: null,
        prime_image: false,
        project_logo: true,
        file_type: "image",
      });
    }
  }

  // 🔹 otherimages
  if (Array.isArray(otherimages)) {
    otherimages.forEach((img) => {
      if (isValidUrl(img?.url)) {
        media.push({
          sourceurl: img.url,
          projectid: projectId,
          attributename: "otherimages",
          imagename: extractFileName(img.url),
          alt_tag: img.altTag || null,
          prime_image: false,
          project_logo: false,
          file_type: "image",
        });
      }
    });
  }

  // 🔹 brochure
  if (Array.isArray(brochure)) {
    brochure.forEach((doc) => {
      if (isValidUrl(doc?.url)) {
        media.push({
          sourceurl: doc.url,
          projectid: projectId,
          attributename: "brochure",
          imagename: extractFileName(doc.url),
          alt_tag: null,
          prime_image: false,
          project_logo: false,
          file_type: "document",
        });
      }
    });
  }

  // 🔹 videos
  if (Array.isArray(videos)) {
    videos.forEach((vid) => {
      if (vid?.VIDEO_ID) {
        const url = `https://www.youtube.com/watch?v=${vid.VIDEO_ID}`;
        media.push({
          sourceurl: url,
          projectid: projectId,
          attributename: "videos",
          imagename: vid.VIDEO_ID,
          alt_tag: null,
          prime_image: false,
          project_logo: false,
          file_type: "video",
        });
      }
    });
  }

  return media;
};


